import {css, styled} from "styled-components";
import type {CSSProperties, ReactNode} from "react";
import {useCallback, useEffect, useLayoutEffect, useRef, useState} from "react";
import {colorPalette} from "../styles/colorPalette";
import {ELLIPSIS, FlexCenterStyle, VerticalFlexStyle, baseDistance, fontSize, fontWeight, radius, zIndex} from "../styles/styles";
import {useAppStore} from "../../../StateManager";
import XmarkLargeIcon from "../icons/xmark-large.svg?react";
import XmarkIcon from "../icons/xmark.svg?react";
import {DomPortal} from "../../modules/abstract/portal/DomPortal";
import type {TransformObj} from "../../../utils/dom/DomUtils";
import {HorizontalAlignment, VerticalAlignment, DomUtils} from "../../../utils/dom/DomUtils";
import type {IButtonV5Props} from "../button/ButtonV5";
import {ButtonStyled, ButtonV5} from "../button/ButtonV5";
import CirclePlusIcon from "../icons/circle-plus.svg?react";
import {Functions} from "../../../utils/function/Functions";
import {IconButtonStyled, IconButtonV5} from "../interaction/IconButtonV5";
import {useClickOutside, useCloseOnKeyboardPress} from "../utils";
import {StepIndicatorStyled} from "../abstract/view/StepIndicatorV5";
import {ReactUtils} from "../../utils/ReactUtils";
import {ObjectUtils} from "../../../utils/data/ObjectUtils";
import {KeyboardListener} from "../../../utils/interaction/key/KeyboardListener";
import {TextInputStyled} from "../details/datatypes/TextInputV5";

interface IButtonProps extends IButtonV5Props {
	hideIcon?: boolean;
}

interface IPopupV5Props {
	readonly label?: ReactNode;
	readonly buttonProps?: IButtonProps;
	readonly parentRef?: HTMLElement;
	readonly children: React.ReactNode;
	readonly verticalAlignment?: VerticalAlignment;
	readonly horizontalAlignment?: HorizontalAlignment;
	readonly centerOnScreen?: boolean;
	readonly noButtons?: boolean;
	readonly width?: string;
	readonly height?: string;
	readonly freezeRoot?: boolean;
	readonly className?: string;
	readonly offsetX?: number;
	readonly offsetY?: number;
	readonly isSmallPopup?: boolean;
	readonly closeOnClickOutside?: boolean;
	readonly style?: CSSProperties;
	readonly arrowIndicator?: "top-center" | "none";
	readonly isIconColorPlatte?: boolean;
	readonly headerButtons?: ReactNode;
	readonly onClick?: (e?: React.MouseEvent) => void;
	readonly onClose?: (e?: React.MouseEvent | KeyboardEvent) => void;
	readonly onMouseOver?: (e?: React.MouseEvent) => void;
	readonly dataCy?: string;
}

interface IPopupStyledProps {
	$center: boolean;
	$width: string;
	$height: string;
	$isSmallPopup: boolean;
}

export const PopupV5 = (props: IPopupV5Props) => {
	const _floating = useRef<HTMLDivElement>(null);
	const appState = useAppStore((state) => state.appState);
	const {
		parentRef,
		verticalAlignment = VerticalAlignment.bottom,
		horizontalAlignment = HorizontalAlignment.outerRight,
		offsetY = -8,
		offsetX = 16,
		isSmallPopup = false,
		freezeRoot = false,
		buttonProps = {
			label: "Create",
			create: Functions.emptyFunction,
			onClick: Functions.emptyFunction,
			disabled: false,
			hideIcon: false,
		} as IButtonProps,
		className = "",
		noButtons = false,
		closeOnClickOutside = true,
		centerOnScreen,
		width,
		height,
		label,
		onClose,
		onClick,
		onMouseOver,
		children,
		style = {},
		arrowIndicator = "none",
		isIconColorPlatte,
		headerButtons,
		dataCy,
	} = props;
	const setIsRootFrozen = useAppStore((state) => state.setIsRootFrozen);
	const [transform, setTransform] = useState<TransformObj>(null);
	const [styleState, setStyleState] = useState<CSSProperties>({});
	const currentUIVersion = appState.currentUIVersion;

	const handleClose = useCallback(() => {
		if (closeOnClickOutside) {
			onClose?.();
		}
	}, [closeOnClickOutside, onClose]);

	const onCloseOnEscape = useCallback(
		(e: KeyboardEvent) => {
			e.stopImmediatePropagation();
			onClose?.();
		},
		[onClose],
	);

	useClickOutside([_floating], handleClose);
	useCloseOnKeyboardPress(onCloseOnEscape, KeyboardListener.KEY_ESCAPE, [], {
		eventType: "keydown",
		onlyLatest: true,
		stopPropagation: true,
	});

	const onClickPopup = (e: React.MouseEvent) => {
		onClick?.();
		e.stopPropagation();
	};

	const setMarginsCallback = useCallback(() => {
		if (centerOnScreen && _floating.current) {
			const newStyleState: CSSProperties = {
				marginLeft: `${(document.body.offsetWidth - _floating.current.offsetWidth) / 2}px`,
				marginTop: `${(document.body.offsetHeight - _floating.current.offsetHeight) / 2}px`,
				...(style ?? {}),
			};
			if (!ObjectUtils.compare(styleState, newStyleState)) {
				setStyleState(newStyleState);
			}
		}
	}, [centerOnScreen, style, styleState]);

	const setTransformCallback = useCallback(() => {
		if (parentRef && _floating.current) {
			const newTransform = DomUtils.getFixedFloatingElementPosition(
				parentRef,
				_floating.current,
				verticalAlignment,
				horizontalAlignment,
				offsetY,
				offsetX,
			);

			if (!ObjectUtils.compare(transform, newTransform)) {
				setTransform(newTransform);
			}
		}
	}, [parentRef, verticalAlignment, horizontalAlignment, offsetY, offsetX, transform]);

	useLayoutEffect(() => {
		setMarginsCallback();
		setTransformCallback();
	}, [setMarginsCallback, setTransformCallback]);

	useEffect(() => {
		if (freezeRoot) {
			setIsRootFrozen(true);
		}

		return () => {
			setIsRootFrozen(false);
		};
	}, [freezeRoot, setIsRootFrozen]);

	const popupStyle: CSSProperties = {
		visibility: centerOnScreen || transform?.translate ? "visible" : "hidden",
		...styleState,
	};

	if (!centerOnScreen && transform) {
		popupStyle.left = `${transform.x}px`;
		popupStyle.top = `${transform.y}px`;
	}

	return (
		<DomPortal destination={appState.app.modalContainer}>
			<div className={ReactUtils.cls({customReportPopup: currentUIVersion === "4.0"})}>
				<PopupStyled
					ref={_floating}
					style={popupStyle}
					$center={centerOnScreen}
					$width={width}
					$height={height}
					className={ReactUtils.cls(className, {topCenterArrowIndicator: arrowIndicator === "top-center"})}
					$isSmallPopup={isSmallPopup}
					onClick={onClickPopup}
					onMouseOver={onMouseOver}
					data-cy={dataCy}
				>
					{onClose && !isIconColorPlatte && (
						<PopupHeaderStyled
							className="header"
							$isSmallPopup={isSmallPopup}
						>
							<NameStyled className="name">{label}</NameStyled>
							<PopupHeaderButtonsStyled>
								{headerButtons && <div className="header-buttons">{headerButtons}</div>}
								<IconButtonV5
									IconComponent={isSmallPopup ? XmarkIcon : XmarkLargeIcon}
									onClick={onClose}
								/>
							</PopupHeaderButtonsStyled>
						</PopupHeaderStyled>
					)}
					<PopupBodyStyled
						className="body"
						$isSmallPopup={isSmallPopup}
					>
						{children}
						{!noButtons &&
							![
								"SpaceCreation",
								"OrganizationCreation",
								"PermissionSetCreation",
								"ReportCreation",
								"ReportRun",
								"SettingsUserCreation",
								"SharingPopup",
								"CustomReport",
							].includes(className) && (
								<CreateButtonStyled className="button popupButton">
									<ButtonV5
										onClick={buttonProps.onClick}
										disabled={buttonProps.disabled}
										label={buttonProps.label}
									>
										{!buttonProps.hideIcon && <CirclePlusIcon />}
									</ButtonV5>
								</CreateButtonStyled>
							)}
					</PopupBodyStyled>
				</PopupStyled>
			</div>
		</DomPortal>
	);
};

export const NameStyled = styled.span`
	font-size: ${fontSize.xl};
	font-weight: ${fontWeight.bold};
	color: ${colorPalette.gray.c950};
	line-height: 24px;
	display: block;
	flex: 1 1 auto;
	min-width: 0;
	max-width: 100%;
	${ELLIPSIS};

	> span {
		display: block;
		min-width: 0;
		max-width: 100%;
		${ELLIPSIS};
	}
`;

export const PopupHeaderStyled = styled.div<{$isSmallPopup?: boolean}>`
	${FlexCenterStyle}
	justify-content: space-between;
	gap: ${baseDistance.sm};
	padding: ${(props) => (props.$isSmallPopup ? baseDistance.sm : baseDistance.md)};

	${NameStyled}:has(${TextInputStyled}) {
		height: auto;

		${TextInputStyled} {
			border-color: transparent;
			margin: 0;
			font-size: 24px;
			line-height: 32px;
			height: auto;
			font-weight: bold;
			${ELLIPSIS};

			&:hover {
				background-color: ${colorPalette.gray.c100};
			}

			&:focus {
				border-color: ${colorPalette.primary.c500Primary};
			}
		}
	}
`;

export const PopupBodyStyled = styled.div<{$isSmallPopup?: boolean}>`
	display: flex;
	flex-direction: column;
	padding: ${(props) =>
		props.$isSmallPopup
			? `0 ${baseDistance.sm} ${baseDistance.sm} ${baseDistance.sm}`
			: `0 ${baseDistance.md} ${baseDistance.md} ${baseDistance.md}`};
	flex: 1;
	min-height: 0;

	.threeColumns {
		display: grid;
		grid-template-columns: 0.5fr 0.65fr 1fr;
	}
`;

const PopupHeaderButtonsStyled = styled.div`
	${FlexCenterStyle}
	justify-content: space-between;
	flex: 0 0 auto;
	gap: ${baseDistance.sm};
`;

const PopupStyled = styled.div<IPopupStyledProps>`
	${VerticalFlexStyle};
	background-color: ${colorPalette.white};
	border-radius: ${(props) => (props.$isSmallPopup ? radius.md : radius.xl)};
	min-width: 320px;
	box-shadow: 0px 4px 20px 0px #00000033;
	z-index: 6002;
	position: absolute;
	visibility: hidden;

	&.topCenterArrowIndicator {
		&::before {
			content: "";
			position: absolute;
			width: 0;
			height: 0;
			border-left: 8px solid transparent;
			border-right: 8px solid transparent;
			border-bottom: 8px solid ${colorPalette.white};
			top: -8px;
			left: calc(50% - 8px);
		}
	}

	${PopupHeaderStyled} {
		${IconButtonStyled} {
			height: 32px;
			min-width: 32px;

			svg {
				width: 24px;
				height: 24px;
			}
		}
	}

	${(props) => {
		if (props.$center) {
			return css`
				position: fixed;
				visibility: visible;
			`;
		}
	}};

	&.SpaceCreation,
	&.ReportCreation {
		min-width: 810px;
		min-height: 600px;
		max-width: 1080px;
		max-height: 800px;
	}

	&.CustomReport {
		${StepIndicatorStyled} {
			width: 320px;
		}

		${ButtonStyled} {
			border-radius: ${radius.sm};
		}
	}

	&.SettingsUserCreation {
		min-width: 528px;
		min-height: 528px;
		max-width: 528px;
		max-height: 528px;
		margin-left: 0px;
		visibility: visible;
	}

	&.PermissionSetCreation {
		min-width: 320px;
		max-width: 320px;
		max-height: 162px;
		position: absolute;
	}

	&.GlobalViewSharing {
		z-index: ${zIndex.popup + zIndex.modal};
		min-height: 280px;
	}

	&.CapturePhoto {
		z-index: ${zIndex.webcamPopup};
	}

	${(props) => css`
		width: ${props.$width || "auto"};
		height: ${props.$height || "auto"};
	`};

	&.SharingPopup {
		.header {
			padding: ${baseDistance.sm};
		}

		.body {
			padding: 0 ${baseDistance.sm} ${baseDistance.sm} ${baseDistance.sm};
		}
	}
`;

export const CreateButtonStyled = styled.div`
	margin-top: 24px;
	display: flex;
	justify-content: flex-end;

	&.button.popupButton {
		cursor: auto;

		.Button &:not(.disabled) {
			cursor: pointer;
		}
	}
`;
